import { button, divider, icon, iconButton, section, tabs } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import { fraction } from "../kit/kit-model.ts";
import { settings } from "./settings.ts";
import { SETTINGS_TABS, type SettingsRow, type SettingsTabId } from "./settings-layout.ts";
import { ACTION_LABELS, bindableKey, defaultBindings, defaultSettings, LIMITS, manualGraphics, rebind, withPreset, type Action, type Graphics, type GraphicsPreset, type Settings } from "./settings-model.ts";

/** Ajuste à mão de uma opção de Gráficos: no Automático, sai dele para o preset que estava valendo (`manualGraphics`). */
const manual = (s: Settings, patch: Partial<Graphics>): Settings => ({ ...s, graphics: manualGraphics(s.graphics, settings.effectivePreset(), patch) });
import { keyLabel, setTutorialEnabled } from "../hud/tutorial-model.ts";
import { loadTutorial, saveTutorial } from "../hud/tutorial-store.ts";

/** Evento da página: o tutorial mudou pelas Opções (a cena relê o guardado). */
export const TUTORIAL_EVENT = "eldrun-tutorial";

type Input = HTMLInputElement | HTMLSelectElement;

/** Elemento visível agora (fora de aba escondida e de seção fechada). */
const visible = (el: HTMLElement): boolean => el.getClientRects().length > 0;

/**
 * Tela de Opções (doc 05 §Configurações; F30), no kit da F80 (ui-h): abas Gráficos, Áudio, Controles e
 * Jogabilidade, seções com ▼ (`settings-layout.ts`), linhas com seletor, controle deslizante e alternador.
 * Cada mudança grava e aplica na hora (ida e volta: tela → estado → navegador), por isso não há "Aplicar":
 * o ✕ fecha e "Restaurar tudo" volta ao padrão. Abre no menu inicial, pela pausa e, em jogo, pela
 * tecla de Opções (P).
 */
export class SettingsPanel {
  readonly element: HTMLElement;
  private readonly fields = new Map<string, Input>();
  private readonly keyButtons = new Map<Action, HTMLButtonElement>();
  private readonly status: HTMLElement;
  private readonly tabBar: { element: HTMLDivElement; select(id: string): void };
  private readonly pages = new Map<SettingsTabId, HTMLElement>();
  private capturing: { action: Action; off: () => void } | undefined;
  /** Esperando a tecla de um remapeamento: a próxima tecla é dela (a cena não trava o ponteiro nela, F99). */
  get capturingKey(): boolean {
    return this.capturing !== undefined;
  }
  /** Quem tinha o foco ao abrir (volta para ele ao fechar). */
  private returnFocus: HTMLElement | undefined;
  onClose: () => void = () => {};

  constructor(parent: HTMLElement) {
    this.status = h("p", { class: "hint opcoes-status", role: "status", id: "opt-status" });
    const rows = this.buildRows();
    this.tabBar = tabs(
      SETTINGS_TABS.map((t) => ({ id: t.id, label: t.label, icon: t.icon })),
      (id) => this.showTab(id as SettingsTabId),
    );
    for (const tab of SETTINGS_TABS) {
      const tabButton = this.tabBar.element.querySelector<HTMLElement>(`[data-aba="${tab.id}"]`);
      const page = h(
        "div",
        { class: "opcoes-pagina", role: "tabpanel", id: `opt-aba-${tab.id}`, "aria-labelledby": tabButton?.id },
        ...tab.sections.map((s) => section(s.title, true, ...s.rows.map((row) => rows.take(row)))),
      );
      tabButton?.setAttribute("aria-controls", page.id);
      this.pages.set(tab.id, page);
    }
    // Toda linha montada tem lugar numa aba (o teste do layout confere a lista; isto pega a montagem).
    rows.assertAllPlaced();

    const reset = button({ label: "Restaurar tudo", icon: "icone-restaurar", variant: "secundario", onClick: () => this.change(() => ({ ...defaultSettings() })) });
    reset.id = "opt-padrao";
    // Um só jeito de fechar (F113): o ✕ do topo (Esc também fecha).
    const closeButton = iconButton({ icon: "icone-x", label: "Fechar", onClick: () => this.hide() });
    closeButton.id = "opt-fechar";
    const titleId = "opt-titulo";
    this.element = h(
      "section",
      { id: "opcoes", class: "k-painel opcoes", role: "dialog", "aria-labelledby": titleId },
      h("header", { class: "k-painel-topo" }, icon("icone-opcoes"), h("h2", { id: titleId, class: "k-titulo" }, "Opções"), closeButton),
      h(
        "div",
        { class: "k-painel-corpo opcoes-corpo" },
        this.tabBar.element,
        h("div", { class: "opcoes-paginas" }, ...this.pages.values()),
        divider(),
        this.status,
        h("div", { class: "opcoes-acoes" }, reset),
      ),
    );
    this.showTab("graficos");
    this.element.hidden = true;
    this.element.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !this.capturing) {
        e.preventDefault();
        this.hide();
      } else if (e.key === "Tab" && !this.capturing) this.trapFocus(e);
    });
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  show(): void {
    this.refresh();
    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    this.element.hidden = false;
    this.element.tabIndex = -1;
    // O foco entra na aba ativa (setas trocam de aba; Tab fica dentro da tela até fechar).
    (this.tabBar.element.querySelector<HTMLElement>('[aria-selected="true"]') ?? this.focusable()[0] ?? this.element).focus?.();
  }

  hide(): void {
    if (this.element.hidden) return;
    this.stopCapture();
    this.element.hidden = true;
    if (this.returnFocus?.isConnected) this.returnFocus.focus?.();
    this.returnFocus = undefined;
    this.onClose();
  }

  /** Mostra uma aba (o clique, as setas da barra ou por fora, como a vitrine). */
  showTab(id: SettingsTabId): void {
    this.tabBar.select(id);
    for (const [tab, page] of this.pages) page.hidden = tab !== id;
  }

  private focusable(): HTMLElement[] {
    return [...this.element.querySelectorAll<HTMLElement>("input, select, button, summary")].filter((el) => !(el as HTMLButtonElement).disabled && el.tabIndex >= 0 && visible(el));
  }

  /** Tab e Shift+Tab circulam dentro da tela de Opções (só no que está à vista). */
  private trapFocus(e: KeyboardEvent): void {
    const list = this.focusable();
    if (list.length === 0) return;
    const i = list.indexOf(document.activeElement as HTMLElement);
    const next = e.shiftKey ? (i <= 0 ? list.length - 1 : i - 1) : i < 0 || i === list.length - 1 ? 0 : i + 1;
    e.preventDefault();
    list[next]!.focus();
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  remove(): void {
    this.stopCapture();
    this.element.remove();
  }

  private change(fn: (s: Settings) => Settings): void {
    settings.set(fn(settings.get()));
    this.refresh();
  }

  /** Mostra o que está guardado (depois de mudar, restaurar ou abrir). */
  private refresh(): void {
    const s = settings.get();
    for (const [, input] of this.fields) (input as unknown as { sync?: (s: Settings) => void }).sync?.(s);
    for (const [action, btn] of this.keyButtons) if (this.capturing?.action !== action) btn.textContent = s.bindings[action].map(keyLabel).join(" ou ");
    const tut = this.element.querySelector<HTMLInputElement>("#opt-tutorial");
    if (tut) tut.checked = loadTutorial().enabled;
  }

  /** Todas as linhas da F30, por id; o layout (`SETTINGS_TABS`) diz onde cada uma vai. */
  private buildRows(): { take(row: SettingsRow): HTMLElement; assertAllPlaced(): void } {
    const volume = (k: "geral" | "musica" | "efeitos" | "ambiente" | "ui", label: string) =>
      this.range(`opt-vol-${k}`, label, 0, 1, 0.05, (s) => s.volumes[k], (s, v) => ({ ...s, volumes: { ...s.volumes, [k]: v } }), (v) => `${Math.round(v * 100)} %`);
    const all: Record<SettingsRow, HTMLElement> = {
      "opt-preset": this.select(
        "opt-preset",
        "Qualidade",
        [["baixo", "Baixo"], ["medio", "Médio"], ["alto", "Alto"], ["automatico", "Automático (mede os primeiros 10 s)"]],
        (s) => s.graphics.preset,
        (s, v) => ({ ...s, graphics: withPreset(s.graphics, v as GraphicsPreset) }),
      ),
      "opt-res": this.range("opt-res", "Escala de resolução", LIMITS.resolution[0], LIMITS.resolution[1], 0.05, (s) => s.graphics.resolution, (s, v) => manual(s, { resolution: v }), (v) => `${Math.round(v * 100)} %`),
      "opt-grass": this.select("opt-grass", "Capim e detalhes", [["0", "Desligado"], ["50", "50 %"], ["100", "100 %"]], (s) => String(s.graphics.grass), (s, v) => manual(s, { grass: v === "0" ? 0 : v === "50" ? 50 : 100 })),
      "opt-dist": this.range("opt-dist", "Distância de visão", LIMITS.distance[0], LIMITS.distance[1], 0.05, (s) => s.graphics.distance, (s, v) => manual(s, { distance: v }), (v) => `${Math.round(v * 100)} %`),
      "opt-fx-aliados": this.select("opt-fx-aliados", "Efeitos dos aliados", [["100", "100 %"], ["60", "60 %"], ["30", "30 %"]], (s) => String(s.allyFx), (s, v) => ({ ...s, allyFx: v === "100" ? 100 : v === "30" ? 30 : 60 })),
      "opt-sombras": this.postCheck("opt-sombras", "Sombras", "shadows"),
      "opt-bloom": this.postCheck("opt-bloom", "Brilho (bloom)", "bloom"),
      "opt-cor": this.postCheck("opt-cor", "Correção de cor", "grade"),
      "opt-vinheta": this.postCheck("opt-vinheta", "Vinheta", "vignette"),
      "opt-luz": this.postCheck("opt-luz", "Luz cinematográfica", "light"),
      "opt-aberracao": this.postCheck("opt-aberracao", "Aberração cromática", "aberration"),
      "opt-lente": this.postCheck("opt-lente", "Reflexo de lente", "flare"),
      "opt-movimento": this.postCheck("opt-movimento", "Desfoque de movimento", "motion"),
      "opt-fov": this.range("opt-fov", "Campo de visão (FOV)", LIMITS.fov[0], LIMITS.fov[1], 1, (s) => s.fov, (s, v) => ({ ...s, fov: v }), (v) => `${v}°`),
      "opt-shake": this.range("opt-shake", "Tremor da câmera", 0, 100, 5, (s) => s.shake, (s, v) => ({ ...s, shake: v }), (v) => `${v} %`),
      "opt-desempenho": this.check("opt-desempenho", "Mostrar FPS e desempenho", (s) => s.showPerf ?? import.meta.env.DEV, (s, v) => ({ ...s, showPerf: v })),
      "opt-vol-geral": volume("geral", "Geral"),
      "opt-vol-musica": volume("musica", "Música"),
      "opt-vol-efeitos": volume("efeitos", "Efeitos"),
      "opt-vol-ambiente": volume("ambiente", "Ambiente"),
      "opt-vol-ui": volume("ui", "Interface"),
      "opt-sens": this.range("opt-sens", "Sensibilidade do mouse", LIMITS.sensitivity[0], LIMITS.sensitivity[1], 0.05, (s) => s.sensitivity, (s, v) => ({ ...s, sensitivity: v }), (v) => `${v.toFixed(2)}×`),
      "opt-inv-x": this.check("opt-inv-x", "Inverter eixo horizontal", (s) => s.invertX, (s, v) => ({ ...s, invertX: v })),
      "opt-inv-y": this.check("opt-inv-y", "Inverter eixo vertical", (s) => s.invertY, (s, v) => ({ ...s, invertY: v })),
      teclas: this.keysTable(),
      "opt-legendas": this.check("opt-legendas", "Legendas de sons importantes, com a direção", (s) => s.captions, (s, v) => ({ ...s, captions: v })),
      "opt-flashes": this.check("opt-flashes", "Reduzir lampejos", (s) => s.reduceFlashes, (s, v) => ({ ...s, reduceFlashes: v })),
      "opt-daltonico": this.check("opt-daltonico", "Modo daltônico (cores seguras, além das formas)", (s) => s.colorblind, (s, v) => ({ ...s, colorblind: v })),
      "opt-tutorial": this.tutorialRow(),
    };
    const left = new Set(Object.keys(all) as SettingsRow[]);
    return {
      take: (row) => {
        left.delete(row);
        return all[row];
      },
      assertAllPlaced: () => {
        if (left.size > 0) throw new Error(`Opções sem aba: ${[...left].join(", ")}`);
      },
    };
  }

  /** Linha com o controle deslizante do kit (o `<input type="range">` com o id e o número ao lado). */
  private range(id: string, label: string, min: number, max: number, step: number, get: (s: Settings) => number, put: (s: Settings, v: number) => Settings, show: (v: number) => string): HTMLElement {
    const out = h("output", { for: id, class: "k-deslizante-valor" });
    const input = h("input", { type: "range", id, min, max, step, class: "k-deslizante-trilha" }) as HTMLInputElement;
    const paint = (v: number) => {
      out.textContent = show(v);
      input.setAttribute("aria-valuetext", show(v));
      input.style.setProperty("--p", String(fraction(v, min, max)));
    };
    (input as unknown as { sync: (s: Settings) => void }).sync = (s) => {
      input.value = String(get(s));
      paint(get(s));
    };
    input.addEventListener("input", () => {
      paint(Number(input.value));
      this.change((s) => put(s, Number(input.value)));
    });
    this.fields.set(id, input);
    return h("div", { class: "k-campo" }, h("label", { for: id, class: "k-campo-nome" }, label), h("div", { class: "k-deslizante" }, input, out));
  }

  /** Linha liga/desliga: a linha inteira é o `<label>` e a caixa é o alternador do kit. */
  private check(id: string, label: string, get: (s: Settings) => boolean, put: (s: Settings, v: boolean) => Settings): HTMLElement {
    const input = h("input", { type: "checkbox", id, class: "k-alternador" }) as HTMLInputElement;
    (input as unknown as { sync: (s: Settings) => void }).sync = (s) => (input.checked = get(s));
    input.addEventListener("change", () => this.change((s) => put(s, input.checked)));
    this.fields.set(id, input);
    return h("label", { class: "k-campo" }, h("span", { class: "k-campo-nome" }, label), input);
  }

  /**
   * Opção do pós-processamento (F52): vale no Médio e no Alto (o Baixo desenha direto); o bloom (no Médio
   * desde a F61) pede alvo em meio-float. Fora disso a caixa fica desligada, com o porquê ao lado.
   */
  private postCheck(id: string, label: string, key: "bloom" | "grade" | "vignette" | "light" | "aberration" | "flare" | "motion" | "shadows"): HTMLElement {
    const input = h("input", { type: "checkbox", id, class: "k-alternador" }) as HTMLInputElement;
    const why = h("span", { class: "hint", id: `${id}-nota` });
    (input as unknown as { sync: (s: Settings) => void }).sync = (s) => {
      const tier = settings.effectivePreset();
      const reason = tier === "baixo" ? "desligado no Baixo" : key !== "bloom" ? "" : !settings.floatTargets() ? "indisponível neste navegador" : "";
      // O que vale (no Automático, o do preset medido; o guardado dele tem os campos do Médio).
      input.checked = (s.graphics.preset === "automatico" ? settings.graphics()[key] : s.graphics[key]) && reason === "";
      input.disabled = reason !== "";
      why.textContent = reason && ` (${reason})`;
    };
    input.addEventListener("change", () => this.change((s) => manual(s, { [key]: input.checked })));
    this.fields.set(id, input);
    return h("label", { class: "k-campo" }, h("span", { class: "k-campo-nome" }, label, why), input);
  }

  private select(id: string, label: string, options: readonly (readonly [string, string])[], get: (s: Settings) => string, put: (s: Settings, v: string) => Settings): HTMLElement {
    const input = h("select", { id, class: "k-seletor" }, ...options.map(([v, text]) => h("option", { value: v }, text))) as HTMLSelectElement;
    (input as unknown as { sync: (s: Settings) => void }).sync = (s) => (input.value = get(s));
    input.addEventListener("change", () => this.change((s) => put(s, input.value)));
    this.fields.set(id, input);
    return h("div", { class: "k-campo" }, h("label", { for: id, class: "k-campo-nome" }, label), input);
  }

  private tutorialRow(): HTMLElement {
    const input = h("input", { type: "checkbox", id: "opt-tutorial", class: "k-alternador" }) as HTMLInputElement;
    input.addEventListener("change", () => {
      saveTutorial(setTutorialEnabled(loadTutorial(), input.checked));
      window.dispatchEvent(new Event(TUTORIAL_EVENT));
    });
    return h("label", { class: "k-campo" }, h("span", { class: "k-campo-nome" }, "Mostrar o tutorial (religar recomeça)"), input);
  }

  private keysTable(): HTMLElement {
    const rows = (Object.keys(ACTION_LABELS) as Action[]).map((action) => {
      const btn = h("button", { type: "button", class: "tecla", "data-action": action, onclick: () => this.capture(action) }) as HTMLButtonElement;
      this.keyButtons.set(action, btn);
      return h("div", { class: "k-campo tecla-linha" }, h("span", { class: "k-campo-nome" }, ACTION_LABELS[action]), btn);
    });
    const restore = button({ label: "Restaurar teclas padrão", icon: "icone-restaurar", variant: "secundario", onClick: () => this.change((s) => ({ ...s, bindings: defaultBindings() })) });
    restore.id = "opt-teclas-padrao";
    return h("div", { class: "teclas-bloco" }, h("div", { class: "teclas" }, ...rows), h("div", { class: "teclas-acoes" }, restore));
  }

  /** Espera a próxima tecla (ou o botão do meio) para a ação; Esc cancela. */
  private capture(action: Action): void {
    this.stopCapture();
    const btn = this.keyButtons.get(action)!;
    btn.textContent = "Pressione uma tecla… (Esc cancela)";
    const done = (code: string | undefined) => {
      this.stopCapture();
      if (!code) {
        this.status.textContent = "";
        this.refresh();
        return;
      }
      const r = rebind(settings.get().bindings, action, code);
      this.change((s) => ({ ...s, bindings: r.bindings }));
      this.status.textContent = r.moved ? `${keyLabel(code)} agora é de "${ACTION_LABELS[action]}"; "${ACTION_LABELS[r.moved]}" ficou com ${r.bindings[r.moved].map(keyLabel).join(" ou ")}.` : `"${ACTION_LABELS[action]}": ${keyLabel(code)}.`;
    };
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === "Escape") return done(undefined);
      // A mesma regra de quando o guardado é lido: 1–8 são do cinto; tecla sem nome não serve.
      if (!bindableKey(e.code)) {
        this.status.textContent = /^Digit[1-8]$/.test(e.code) ? "1 a 8 são do cinto: escolha outra tecla." : `${keyLabel(e.code)} não pode ser usada: escolha outra tecla.`;
        return;
      }
      done(e.code);
    };
    const onMouse = (e: MouseEvent) => {
      if (e.button !== 1 && e.button !== 2) return;
      e.preventDefault();
      done(e.button === 1 ? "MouseMiddle" : "MouseRight");
    };
    window.addEventListener("keydown", onKey, { capture: true });
    window.addEventListener("mousedown", onMouse, { capture: true });
    this.capturing = {
      action,
      off: () => {
        window.removeEventListener("keydown", onKey, { capture: true });
        window.removeEventListener("mousedown", onMouse, { capture: true });
      },
    };
  }

  private stopCapture(): void {
    this.capturing?.off();
    this.capturing = undefined;
  }
}

let shared: SettingsPanel | undefined;
/** A tela de Opções da página (a mesma no menu e em jogo). */
export function sharedSettingsPanel(): SettingsPanel {
  shared ??= new SettingsPanel(document.body);
  return shared;
}
